
import { useEffect } from 'react';
import Footer from '../../../components/feature/Footer';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';

export default function SellPropertyCodeViolationsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <GlobalNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5accc4bd58b0295805ede4ca56e2d804.jpg"
            alt="Property with code violations in El Paso"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
            Code Violation Solutions
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            How to Sell Property with Code Violations in El Paso
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            A comprehensive guide for El Paso homeowners dealing with municipal code violations, fines, and the challenge of selling a non-compliant property
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Cash Offer Today
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              (915) 206-5814
            </a>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Receiving a code violation notice from the City of El Paso can feel overwhelming, especially when you're already considering selling your property. Whether it's overgrown vegetation, structural issues, unpermitted additions, or accumulated debris, code violations can complicate the selling process and create significant financial stress. The good news is that you have options—and selling a property with code violations is absolutely possible when you understand your choices.
            </p>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              El Paso's Code Enforcement Division actively monitors properties throughout the city, from the historic neighborhoods near Downtown to the newer developments in the Northeast and Far East. With the city's focus on maintaining community standards and property values, violations can accumulate quickly if left unaddressed. Many homeowners find themselves caught between the cost of repairs and the urgency of selling, unsure of the best path forward.
            </p>

            <p className="text-gray-700 leading-relaxed">
              This comprehensive guide will walk you through everything you need to know about selling a property with code violations in El Paso—from understanding common violations and their consequences to exploring your selling options and finding the right solution for your situation. Whether you're dealing with a single violation or multiple citations, you'll discover practical strategies to move forward and get your property sold.
            </p>
          </div>
        </div>
      </section>

      {/* What Are Code Violations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Understanding Code Violations in El Paso
          </h2>
          
          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=city%20code%20enforcement%20officer%20in%20uniform%20inspecting%20residential%20property%20exterior%20with%20clipboard%20taking%20notes%20el%20paso%20texas%20neighborhood%20warm%20afternoon%20lighting%20professional%20documentary%20photography%20showing%20municipal%20inspection%20process&width=1200&height=600&seq=code-violations-inspection-002&orientation=landscape"
              alt="Code enforcement inspection in El Paso"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Code violations occur when a property fails to meet the standards established by the City of El Paso's municipal codes. These codes exist to protect public health, safety, and welfare while maintaining neighborhood aesthetics and property values. The city's Code Enforcement Division, part of the Environmental Services Department, is responsible for investigating complaints and ensuring compliance.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Common Types of Code Violations in El Paso</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              El Paso homeowners encounter various types of code violations, each with different implications for selling. Understanding what you're dealing with is the first step toward resolution. If your property has <a href="/elpaso-tx/cant-afford-repairs-to-sell-house" className="text-teal-600 hover:text-teal-700 font-semibold">deferred maintenance issues that make repairs unaffordable</a>, code violations often follow.
            </p>

            <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Property Maintenance Violations</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Overgrown vegetation:</strong> Grass exceeding 12 inches, weeds, dead trees, or vegetation obstructing sidewalks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Exterior deterioration:</strong> Peeling paint, rotting wood, broken windows, damaged siding, or crumbling stucco</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Roof damage:</strong> Missing shingles, visible holes, sagging sections, or improper repairs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Fence violations:</strong> Broken, leaning, or deteriorated fencing that poses safety hazards</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Structural and Safety Violations</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Foundation issues:</strong> Visible cracks, settling, or structural instability</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Electrical hazards:</strong> Exposed wiring, unpermitted electrical work, or outdated systems</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Plumbing problems:</strong> Sewage issues, water damage, or non-functional systems</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Fire hazards:</strong> Blocked exits, missing smoke detectors, or combustible material storage</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Zoning and Permit Violations</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Unpermitted construction:</strong> Room additions, garages, carports, or structures built without permits</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Illegal conversions:</strong> Garages converted to living spaces without proper permits</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Setback violations:</strong> Structures built too close to property lines</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Zoning non-compliance:</strong> Using residential property for commercial purposes</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Nuisance and Environmental Violations</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Junk and debris:</strong> Accumulated trash, abandoned vehicles, or excessive outdoor storage</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Graffiti:</strong> Vandalism that hasn't been removed within required timeframes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Stagnant water:</strong> Pools, containers, or areas collecting standing water (mosquito breeding)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-line text-teal-600 mr-3 mt-1"></i>
                  <span><strong>Hazardous materials:</strong> Improper storage of chemicals, paints, or other dangerous substances</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Consequences Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            The Real Consequences of Ignoring Code Violations
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=official%20city%20code%20violation%20notice%20letter%20and%20fine%20documents%20on%20desk%20with%20calculator%20showing%20penalty%20amounts%20el%20paso%20texas%20municipal%20enforcement%20paperwork%20warm%20natural%20lighting%20professional%20documentary%20photography&width=1200&height=600&seq=code-violations-fines-003&orientation=landscape"
              alt="Code violation fines and notices"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Many homeowners underestimate the serious consequences of unresolved code violations. What starts as a simple notice can quickly escalate into a significant financial and legal burden. Understanding these consequences helps illustrate why addressing violations—or selling to someone who will—is so important.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Financial Penalties That Accumulate</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              The City of El Paso imposes fines for code violations that can range from $50 to $2,000 per day, depending on the violation type and duration. These fines don't just add up—they can become <a href="/elpaso-tx/property-liens-guide" className="text-teal-600 hover:text-teal-700 font-semibold">liens against your property</a>, making it impossible to sell through traditional channels without first satisfying the debt.
            </p>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8">
              <h4 className="text-lg font-bold text-red-800 mb-3">Warning: Escalating Penalties</h4>
              <ul className="space-y-2 text-red-700">
                <li>• First violation notice: Warning with compliance deadline (typically 10-30 days)</li>
                <li>• Non-compliance: Fines begin at $50-$500 per violation</li>
                <li>• Continued non-compliance: Daily fines up to $2,000</li>
                <li>• Extended non-compliance: Municipal court summons and potential criminal charges</li>
                <li>• Extreme cases: City may perform abatement and place lien for costs plus penalties</li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Impact on Property Sale</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Code violations create significant obstacles when trying to sell your property through traditional methods. Most buyers using conventional financing will have their lenders require a clear inspection, and violations will typically need to be resolved before closing. This is similar to challenges faced by homeowners with <a href="/title-issues" className="text-teal-600 hover:text-teal-700 font-semibold">title complications or legal encumbrances</a>.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Traditional Sale Challenges</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Buyers' lenders may refuse to finance</li>
                  <li>• Home inspections reveal violations</li>
                  <li>• Title companies flag outstanding liens</li>
                  <li>• Appraisals come in lower due to condition</li>
                  <li>• Buyers negotiate steep discounts</li>
                  <li>• Deals fall through at closing</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Legal Consequences</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>• Municipal court appearances required</li>
                  <li>• Potential criminal misdemeanor charges</li>
                  <li>• Judgments recorded against you</li>
                  <li>• Wage garnishment possible</li>
                  <li>• Credit score damage</li>
                  <li>• Difficulty obtaining future permits</li>
                </ul>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              For homeowners already <a href="/avoid-foreclosure" className="text-teal-600 hover:text-teal-700 font-semibold">facing foreclosure or financial hardship</a>, code violation fines can accelerate the crisis. The combination of mortgage payments, property taxes, and accumulating fines creates an unsustainable situation that requires immediate action.
            </p>
          </div>
        </div>
      </section>

      {/* Your Options Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Your Options for Selling a Property with Code Violations
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=homeowner%20couple%20meeting%20with%20real%20estate%20professional%20at%20kitchen%20table%20discussing%20property%20options%20documents%20and%20laptop%20visible%20modern%20southwestern%20home%20interior%20warm%20natural%20lighting%20showing%20consultation%20and%20decision%20making%20professional%20photography&width=1200&height=600&seq=code-violations-options-004&orientation=landscape"
              alt="Discussing property selling options"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              When you own a property with code violations, you have several paths forward. The right choice depends on your financial situation, timeline, and how much effort you're willing to invest. Let's explore each option in detail so you can make an informed decision.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Option 1: Fix the Violations and Sell Traditionally</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              If you have the financial resources and time, addressing the violations before listing your property can maximize your sale price. This approach works best for minor violations that can be resolved quickly and affordably.
            </p>

            <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
              <h4 className="text-lg font-bold text-teal-600 mb-4">Steps to Resolve Violations</h4>
              <ol className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">1</span>
                  <span><strong>Contact Code Enforcement:</strong> Request a complete list of all violations and understand exactly what needs to be corrected</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">2</span>
                  <span><strong>Get repair estimates:</strong> Obtain quotes from licensed contractors for all required work</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">3</span>
                  <span><strong>Pull necessary permits:</strong> Ensure all repair work is properly permitted to avoid future issues</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">4</span>
                  <span><strong>Complete repairs:</strong> Have work done by qualified professionals</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">5</span>
                  <span><strong>Request re-inspection:</strong> Schedule city inspection to verify compliance and close the case</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold mr-4">6</span>
                  <span><strong>Pay any outstanding fines:</strong> Clear all penalties before listing</span>
                </li>
              </ol>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-green-800 mb-3">
                  <i className="ri-check-line mr-2"></i>Pros
                </h4>
                <ul className="space-y-2 text-green-700">
                  <li>• Maximize sale price</li>
                  <li>• Access to all buyer types</li>
                  <li>• Traditional financing available</li>
                  <li>• Clean title at closing</li>
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-red-800 mb-3">
                  <i className="ri-close-line mr-2"></i>Cons
                </h4>
                <ul className="space-y-2 text-red-700">
                  <li>• Significant upfront costs</li>
                  <li>• Time-consuming process</li>
                  <li>• No guarantee of ROI</li>
                  <li>• Permit delays possible</li>
                </ul>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Option 2: Sell As-Is to a Cash Buyer</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              For many El Paso homeowners with code violations, selling to a <a href="/elpaso-tx/cash-home-buyers-el-paso" className="text-teal-600 hover:text-teal-700 font-semibold">reputable cash home buyer</a> is the most practical solution. Cash buyers specialize in purchasing properties in any condition and can close quickly without requiring you to make repairs or resolve violations first.
            </p>

            <div className="bg-teal-50 border-l-4 border-teal-500 p-6 rounded-r-lg mb-8">
              <h4 className="text-lg font-bold text-teal-800 mb-3">Why Cash Buyers Work for Code Violation Properties</h4>
              <p className="text-teal-700 mb-4">
                Professional real estate investors who buy houses for cash understand how to navigate code violations. They have experience working with the City of El Paso, know how to resolve violations efficiently, and factor these costs into their offers. This means you can sell your property without spending a dime on repairs or dealing with the city yourself.
              </p>
              <p className="text-teal-700">
                Learn more about <a href="/elpaso-tx/investors-buy-house-as-is" className="text-teal-600 hover:text-teal-700 font-semibold underline">how investors evaluate and purchase as-is properties</a>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-green-800 mb-3">
                  <i className="ri-check-line mr-2"></i>Pros
                </h4>
                <ul className="space-y-2 text-green-700">
                  <li>• No repairs needed</li>
                  <li>• Close in as little as 7 days</li>
                  <li>• No inspections or appraisals</li>
                  <li>• Buyer handles violations</li>
                  <li>• Guaranteed closing</li>
                  <li>• No commissions or fees</li>
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-6">
                <h4 className="text-lg font-bold text-red-800 mb-3">
                  <i className="ri-close-line mr-2"></i>Cons
                </h4>
                <ul className="space-y-2 text-red-700">
                  <li>• Lower sale price than retail</li>
                  <li>• Must vet buyer carefully</li>
                  <li>• Some buyers are unscrupulous</li>
                </ul>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Option 3: List with a Real Estate Agent (Disclosure Required)</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              You can list a property with code violations through a traditional real estate agent, but you must disclose all known violations to potential buyers. This approach may work if violations are minor and you're willing to negotiate on price or offer repair credits.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              If you're considering this route, we can <a href="/elpaso-tx/hire-realtor-el-paso" className="text-teal-600 hover:text-teal-700 font-semibold">connect you with experienced El Paso real estate agents</a> who understand how to market properties with challenges. However, be prepared for a longer selling timeline and potential buyer hesitation.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Option 4: Negotiate with the City</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              In some cases, you may be able to negotiate with El Paso's Code Enforcement Division for extended compliance timelines or reduced fines. This is particularly relevant if you're actively working to sell the property and can demonstrate good faith efforts to resolve issues.
            </p>

            <p className="text-gray-700 leading-relaxed">
              The city sometimes offers payment plans for accumulated fines, similar to <a href="/elpaso-tx/property-tax-payment-plans-deferral" className="text-teal-600 hover:text-teal-700 font-semibold">property tax payment arrangements</a>. Contact the Code Enforcement office at (915) 212-0000 to discuss your specific situation.
            </p>
          </div>
        </div>
      </section>

      {/* Step-by-Step Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            How to Sell Your Code Violation Property Fast
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=real%20estate%20investor%20reviewing%20property%20documents%20with%20homeowner%20signing%20contract%20at%20kitchen%20table%20successful%20home%20sale%20transaction%20modern%20southwestern%20home%20interior%20warm%20natural%20lighting%20professional%20photography%20showing%20closing%20process&width=1200&height=600&seq=code-violations-process-005&orientation=landscape"
              alt="Selling property with code violations process"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              If you've decided that selling to a cash buyer is the right choice for your situation, here's exactly how the process works when you work with TREX RE LLC. We've helped countless El Paso homeowners sell properties with code violations, and our streamlined process makes it simple.
            </p>

            <div className="space-y-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Contact Us for a Free Consultation</h3>
                  <p className="text-gray-700">
                    Call us at (915) 206-5814 or fill out our online form. Tell us about your property and the code violations you're facing. We'll ask a few questions to understand your situation—there's no obligation and no pressure.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">We Research the Violations</h3>
                  <p className="text-gray-700">
                    Our team will research all open code violations, liens, and fines associated with your property. We work directly with the City of El Paso to understand exactly what's required for compliance and factor these costs into our evaluation.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Receive Your Cash Offer</h3>
                  <p className="text-gray-700">
                    Within 24-48 hours, we'll present you with a fair, no-obligation cash offer. Our offer accounts for the cost of resolving violations, so you know exactly what you'll walk away with—no surprises at closing.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Closing Date</h3>
                  <p className="text-gray-700">
                    If you accept our offer, you choose the closing date that works for your schedule. Need to close in 7 days? We can do that. Need 30 days to make arrangements? That works too. You're in control.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-6">
                  5
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Close and Get Paid</h3>
                  <p className="text-gray-700">
                    We handle all the paperwork and work with a reputable local title company to ensure a smooth closing. You receive your cash, and we take over responsibility for resolving all code violations. It's that simple.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Special Situations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Special Situations: Code Violations Combined with Other Challenges
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=distressed%20property%20exterior%20showing%20multiple%20issues%20overgrown%20yard%20damaged%20roof%20peeling%20paint%20el%20paso%20texas%20desert%20neighborhood%20warm%20afternoon%20lighting%20professional%20real%20estate%20photography%20showing%20property%20needing%20extensive%20work&width=1200&height=600&seq=code-violations-special-006&orientation=landscape"
              alt="Property with multiple challenges"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              Code violations rarely exist in isolation. Many El Paso homeowners dealing with code enforcement are also facing other challenges that complicate the selling process. Here's how we handle common combined situations:
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Code Violations + Inherited Property</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              If you've <a href="/inherited-homes" className="text-teal-600 hover:text-teal-700 font-semibold">inherited a property with existing code violations</a>, you may be facing fines that accumulated before you even took ownership. Texas law can hold current owners responsible for violations regardless of when they occurred. We specialize in helping heirs navigate these complex situations, especially when combined with <a href="/elpaso-tx/texas-probate-guide" className="text-teal-600 hover:text-teal-700 font-semibold">probate requirements</a>.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Code Violations + Tax Delinquency</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Properties with code violations often also have <a href="/elpaso-tx/owing-too-much-property-tax" className="text-teal-600 hover:text-teal-700 font-semibold">delinquent property taxes</a>. When you're facing both code enforcement fines and tax liens, the financial pressure can feel overwhelming. We can purchase properties with multiple liens and handle all outstanding obligations at closing.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Code Violations + Tenant Issues</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Landlords sometimes receive code violations for properties occupied by difficult tenants who refuse to maintain the premises or allow access for repairs. If you're dealing with <a href="/elpaso-tx/we-buy-houses-with-tenants" className="text-teal-600 hover:text-teal-700 font-semibold">non-paying tenants and code violations</a>, we can purchase your rental property and handle both the tenant situation and the violations.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Code Violations + Hoarding Situations</h3>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Hoarding often leads to multiple code violations, from structural damage to pest infestations to fire hazards. These situations require compassion and expertise. Learn how we help families <a href="/elpaso-tx/sell-hoarder-houses" className="text-teal-600 hover:text-teal-700 font-semibold">sell hoarded properties without any cleanup required</a>.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-6">Code Violations + Fire or Storm Damage</h3>
            
            <p className="text-gray-700 leading-relaxed">
              Properties damaged by fire, flooding, or severe weather often accumulate code violations while owners struggle to afford repairs or navigate insurance claims. We <a href="/elpaso-tx/we-buy-fire-damaged-property" className="text-teal-600 hover:text-teal-700 font-semibold">purchase fire-damaged and storm-damaged properties</a> regardless of code violation status.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions About Selling with Code Violations
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20consultation%20meeting%20in%20modern%20office%20homeowner%20asking%20questions%20to%20investor%20expert%20warm%20natural%20lighting%20showing%20helpful%20discussion%20and%20problem%20solving%20professional%20photography&width=1200&height=600&seq=code-violations-faq-007&orientation=landscape"
              alt="FAQ consultation about code violations"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I legally sell a house with open code violations in Texas?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, you can legally sell a property with code violations in Texas. However, you are required to disclose all known violations to potential buyers. The violations don't prevent the sale, but they may limit your buyer pool to cash investors who specialize in purchasing properties with challenges. Traditional buyers using financing may not be able to close on a property with unresolved violations.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Will code violation fines transfer to the new owner?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Code violation fines that have become liens against the property will need to be satisfied at closing, typically from the sale proceeds. However, personal fines assessed against you as the property owner may remain your responsibility even after selling. When you sell to TREX RE LLC, we handle all property-related liens and work to resolve outstanding issues as part of the transaction.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How much do code violations reduce my home's value?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                The impact on value depends on the type and severity of violations. Minor violations like overgrown vegetation might reduce value by a few thousand dollars, while major structural violations or unpermitted additions could reduce value by 20-40% or more. Cash buyers factor in the cost of repairs, permits, fines, and the time required to resolve issues when making offers.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What happens if I ignore code violations and don't sell?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Ignoring code violations leads to escalating fines, potential criminal charges, and liens against your property. In extreme cases, the City of El Paso can perform abatement (make repairs themselves) and place a lien for all costs plus penalties. These liens accrue interest and can eventually lead to foreclosure. It's always better to address violations proactively or sell to someone who will.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I get a permit for unpermitted work after the fact?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Yes, you can often obtain retroactive permits for unpermitted work, but the process can be expensive and complicated. The city may require you to open walls for inspection, bring work up to current code standards, or even remove non-compliant additions. For many homeowners, the cost of retroactive permitting exceeds the value it adds, making selling as-is a more practical choice.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How quickly can I sell a house with code violations?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                When selling to a cash buyer like TREX RE LLC, you can close in as little as 7 days. We don't require you to resolve violations before closing—we handle everything after purchase. Traditional sales with code violations can take months or even years, as you'll need to either fix the violations first or find a buyer willing to take on the challenges.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Do I need to clean out the property before selling?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                When selling to TREX RE LLC, you don't need to clean, repair, or remove anything from the property. We buy houses in any condition, including those filled with belongings, debris, or items contributing to code violations. Take what you want and leave the rest—we'll handle everything else.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What if I have multiple code violations from different departments?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Properties can have violations from multiple city departments—Code Enforcement, Fire Marshal, Health Department, and Building Inspection. We have experience dealing with all types of violations and can coordinate resolution across departments. Our team researches all open cases before making an offer, so there are no surprises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Related Resources for El Paso Homeowners
          </h2>

          <div className="mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=stack%20of%20real%20estate%20guides%20and%20resource%20documents%20on%20clean%20desk%20with%20laptop%20showing%20property%20information%20el%20paso%20texas%20warm%20natural%20lighting%20professional%20photography%20showing%20helpful%20resources%20and%20information&width=1200&height=600&seq=code-violations-resources-008&orientation=landscape"
              alt="El Paso real estate resources"
              className="w-full h-80 object-cover object-top rounded-xl mb-8"
            />
          </div>

          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-gray-700 leading-relaxed mb-8">
              Understanding your options is the first step toward resolving your property challenges. Here are additional resources that may help you navigate your situation:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <a href="/elpaso-tx/sell-house-fast" className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How to Sell Your House Fast in El Paso</h3>
              <p className="text-gray-600 text-sm">Complete guide to selling quickly, comparing all your options</p>
            </a>
            <a href="/elpaso-tx/epcad" className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 mb-2">El Paso Central Appraisal District (EPCAD)</h3>
              <p className="text-gray-600 text-sm">Property tax information and exemption resources</p>
            </a>
            <a href="/elpaso-tx/texas-homestead-exemptions-protections" className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Texas Homestead Exemptions & Protections</h3>
              <p className="text-gray-600 text-sm">Learn about property tax savings and creditor protections</p>
            </a>
            <a href="/elpaso-tx/el-paso-tax-foreclosure-information-deadlines" className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer">
              <h3 className="text-lg font-bold text-gray-900 mb-2">El Paso Tax Foreclosure Information</h3>
              <p className="text-gray-600 text-sm">Critical deadlines and how to protect your property</p>
            </a>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">City of El Paso Code Enforcement Contact</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-700 mb-2"><strong>Phone:</strong> (915) 212-0000</p>
                <p className="text-gray-700 mb-2"><strong>Address:</strong> 801 Texas Ave, El Paso, TX 79901</p>
                <p className="text-gray-700"><strong>Hours:</strong> Monday-Friday, 8:00 AM - 5:00 PM</p>
              </div>
              <div>
                <p className="text-gray-700 mb-2"><strong>Online:</strong> <a href="https://www.elpasotexas.gov" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">www.elpasotexas.gov</a></p>
                <p className="text-gray-700"><strong>Report Violations:</strong> 311 or (915) 212-0000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Sell Your Property with Code Violations?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Don't let code violations hold you back. Get a fair cash offer today and let us handle the violations while you move on with your life.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/get-cash-offer-for-my-property"
              className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Your Cash Offer
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a
              href="tel:+1-915-206-5814"
              className="inline-flex items-center px-8 py-4 bg-teal-700 text-white font-semibold rounded-lg hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (915) 206-5814
            </a>
          </div>
          <p className="text-teal-200 mt-6 text-sm">
            No obligation • No repairs needed • Close on your timeline
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
